import { nowIsoUtc } from "@nijiurachan/js/util/time";
import { useLayoutEffect, useRef } from "react";
import { isAdultMixBoard } from "@/config/mixLineup";
import {
  completeDockingOnboarding,
  shouldShowDockingOnboarding,
} from "@/features/mix-onboarding/lib/dockingOnboarding";
import type { PwaInstallPromptStore } from "@/features/mix-onboarding/lib/pwaInstallPrompt";
import {
  createMediator,
  type EventNode,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import { parseMixRouteThreadSlug } from "@/shared/lib/threadRoutePath";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

type GateEvent =
  | { type: "docking-gate.complete"; isAdult: boolean }
  | { type: "docking-gate.sync"; isDesktop: boolean };
type GateEffect =
  | { type: "complete"; isAdult: boolean }
  | { type: "stop-capture" };
type DockingGateState = { isOpen: boolean; completedAdult: boolean | null };

export function useDockingGatePresenter(
  isMixCatalog: boolean,
  isDesktop: boolean,
  pathname: string,
  installPromptStore: PwaInstallPromptStore,
  enabledSlugs: readonly string[] = [],
): {
  node: EventNode<TypedEvent>;
  isOpen: boolean;
  requiresAdultConfirmation: boolean;
  onComplete: (isAdult: boolean) => void;
} {
  const directThreadSlug = parseMixRouteThreadSlug(pathname);
  const requiresAdultConfirmation =
    directThreadSlug !== undefined && isAdultMixBoard(directThreadSlug);
  const includesAdultBoard =
    requiresAdultConfirmation || enabledSlugs.some(isAdultMixBoard);
  const latestStore = useRef(installPromptStore);
  const mediator = useHierarchyResource((previous) => {
    const restoredState = previous?.isDisposed
      ? getHierarchyResourceSnapshot<DockingGateState>(previous)
      : undefined;
    return createMediator<DockingGateState, GateEvent, GateEffect>(
      restoredState ?? {
        isOpen:
          isMixCatalog &&
          shouldShowDockingOnboarding(undefined, includesAdultBoard),
        completedAdult: null,
      },
      (state, event) =>
        event.type === "docking-gate.complete"
          ? {
              status: "handled",
              state: { isOpen: false, completedAdult: event.isAdult },
              effects: [{ type: "complete", isAdult: event.isAdult }],
            }
          : {
              status: "handled",
              state,
              effects:
                !state.isOpen || event.isDesktop
                  ? [{ type: "stop-capture" }]
                  : [],
            },
      {
        runEffect(effect: GateEffect): void {
          latestStore.current.stopCapture();
          if (effect.type === "complete")
            completeDockingOnboarding(nowIsoUtc(), effect.isAdult);
        },
      },
    );
  });
  const gateState = useMediatorSnapshot(mediator);
  const isOpen =
    isMixCatalog &&
    (gateState.isOpen ||
      (gateState.completedAdult === null
        ? shouldShowDockingOnboarding(undefined, includesAdultBoard)
        : includesAdultBoard && !gateState.completedAdult));
  const node = useComponentNode((event) => {
    if (
      event.type !== "docking-gate.complete" &&
      event.type !== "docking-gate.sync"
    )
      return "unhandled";
    return mediator.dispatch(event as GateEvent).status === "handled"
      ? "handled"
      : "unhandled";
  });
  useLayoutEffect(() => {
    latestStore.current = installPromptStore;
    const event: GateEvent = { type: "docking-gate.sync", isDesktop };
    node.dispatch(event);
  }, [node, isDesktop, installPromptStore]);
  return {
    node,
    isOpen,
    requiresAdultConfirmation,
    onComplete(isAdult: boolean): void {
      const event: GateEvent = { type: "docking-gate.complete", isAdult };
      node.dispatch(event);
    },
  };
}
